import camelCase from 'lodash.camelcase';
import { annotate } from 'rough-notation';
import { RoughAnnotation } from 'rough-notation/lib/model';
import { CLASSNAME_PREFIX, DEFAULT_ANNOTATION_OPTIONS } from 'src/constants';
import { BaseConfig, THighlight } from 'src/types';
import { getCoords } from './util';
import { searchWithXPathDeep } from '../helpers';
/** Highlight rect for tracking and similarity detection */
interface HighlightRect {
  id: string;
  top: number;
  left: number;
  width: number;
  height: number;
  timestamp: number;
  text?: string;
}

export default class NotationHighlight {
  static readonly classNamePrefix = `${CLASSNAME_PREFIX}hl-`;
  public id: string;
  public time: number;
  public annotation?: RoughAnnotation & {
    shown?: boolean;
    _svg?: HTMLElement;
  };
  public nInSeries: number;
  public counter?: number;
  private prevTop?: number;
  public redraw?: () => void;
  public element?: HTMLElement;
  private startElement?: HTMLElement;
  private endElement?: HTMLElement;
  private stickyParent?: HTMLElement | null;
  private isShown = false;
  private static shownHighlights: Set<string>;
  private static highlightRects: HighlightRect[] = [];
  private isEditor: boolean;

  public static clearShownHighlights(): void {
    NotationHighlight.shownHighlights.clear();
  }
  public idElements: HTMLDivElement[] = [];
  private isInvisible = false;
  public toggle() {
    if (this.isShown) {
      this.hide();
    } else {
      this.show();
    }
  }
  public show() {
    if (!this.element) {
      return this.log(new Error('Element not found'));
    }
    this.isShown = true;
    // this.element.style.border = "3px solid red";
    this.element.style.visibility = 'visible';
  }
  public hide() {
    if (!this.element) {
      return this.log(new Error('Element not found'));
    }
    this.isShown = false;
    this.element.style.visibility = 'hidden';
  }
  public showAnnotation() {
    if (!this.annotation) {
      return;
    }
    this.annotation.show();
    this.annotation.shown = true;
  }

  public removeAnnotation() {
    if (!this.annotation) {
      return this.log(new Error('Annotation not created yet'));
    }
    this.annotation.remove();
  }

  public hideAnnotation() {
    if (!this.annotation) {
      return this.log(new Error('Annotation not created yet'));
    }
    this.annotation.shown = false;
    this.annotation.hide();
  }

  public addEventListener(event: string, callback: () => void) {
    if (!this.element) {
      return this.log(new Error('Element not found'));
    }
    this.element.addEventListener(event, callback);
  }
  public removeEventListener(event: string, callback: () => void) {
    if (!this.element) {
      return this.log(new Error('Element not found'));
    }
    this.element.removeEventListener(event, callback);
  }

  public destroy() {
    if (!this.element) return;
    this.annotation?.remove();
    this.element.remove();
  }

  get isShowing() {
    return this.annotation?.isShowing();
  }

  public get highlightDetails() {
    let position;

    if (this.element) {
      position = getCoords(this.element);
    }

    return { highlight: this.highlight, position, element: this.startElement };
  }

  constructor(
    public readonly highlight: THighlight,
    private readonly config?: BaseConfig,
    // TODO: type properly when merged with develop
    private readonly cuepoint?: Record<string, unknown>,
    private readonly options?: {
      animate?: boolean;
      createAnnotations?: boolean;
      isEditor?: boolean;
    },
    private readonly onHighlightNotFound?: (
      highlightId: string,
      error: Error,
    ) => void,
    private readonly onHighlightInvisible?: (
      highlightId: string,
      element: HTMLElement,
    ) => void,
    private readonly onHighlightAppear?: (
      highlightId: string,
      element: HTMLElement,
    ) => void,
  ) {
    this.isEditor = options?.isEditor ?? true;
    this.id = highlight.id;
    this.time = highlight.time ?? (cuepoint?.['start'] as number);
    this.nInSeries = highlight.nInSeries ?? 0;
    this.counter = highlight.counter;
    NotationHighlight.shownHighlights = new Set();
    this.createHighlightElement();
  }
  public scrollTo() {
    this.element?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    });
  }

  public move(counter: number) {
    const style = this.calculatePosition();
    this.setStyle(style);
    if (this.stickyParent && style.position === 'fixed') {
      const svg = this?.annotation?._svg;
      if (svg) {
        svg.style.position = 'fixed';
      }
    }
    const wasShown = this.annotation?.shown ?? false;
    // Recreate the id element with the new counter if the counter argument is provided
    this.idElements.forEach((idElement) => {
      if (counter) {
        idElement.innerHTML = counter.toString();
      }
    });
    if (wasShown) {
      this.showAnnotation();
    }
  }

  private calculatePosition(
    startElement?: HTMLElement,
  ): Partial<CSSStyleDeclaration> {
    const { highlight } = this;
    const startSelector = highlight.selector?.start;
    const textContent = highlight.selector?.text;
    if (startElement) {
      this.startElement = startElement;
    }
    const endSelector = highlight.selector?.end;

    // If we have a start selector, try to find the parent element first
    if (startSelector) {
      const escapedSelector = escapeCSS(startSelector);
      const parentElement = window.document.querySelector(
        escapedSelector,
      ) as HTMLElement;

      if (parentElement) {
        console.debug('Found parent element by selector', parentElement);

        // If we have text content, search for it within the parent element
        if (textContent) {
          const textElement = this.findElementByTextWithinParent(
            parentElement,
            textContent,
          );
          if (textElement) {
            this.startElement = textElement;
            console.debug(
              'Found element by text within parent',
              this.startElement,
            );
          } else {
            // handle text not found - fallback to using the parent element itself
            this.startElement = parentElement;
            console.debug(
              'Text not found, using parent element as fallback',
              this.startElement,
            );
          }
        } else {
          // No text content, use the parent element directly
          this.startElement = parentElement;
          console.debug(
            'No text content, using parent element',
            this.startElement,
          );
        }
      } else {
        console.debug('Parent element not found by selector', startSelector);
      }
    } else if (textContent) {
      // No start selector but we have text content, search directly for the text
      console.debug(
        'No start selector provided, searching directly for text:',
        textContent,
      );
      const textElement = this.findElementByTextInDocument(textContent);
      if (textElement) {
        this.startElement = textElement;
        console.debug('Found element by direct text search', this.startElement);
      }
    }
    if (!this.startElement && highlight.generated) {
      if (this.onHighlightNotFound) {
        this.onHighlightNotFound(
          highlight.id,
          new Error(`Element not found for highlight with id ${highlight.id}`),
        );
        return {};
      }
      return this.log(
        new Error(`Element not found for highlight with id ${highlight.id}`),
      );
    }
    if (startSelector === 'body') {
      return this.log(
        new Error(
          `Cannot highlight the body element, check highlight with id ${highlight.id}`,
        ),
      );
    }

    // RETURNING OBJECT BECAUSE OF THE METHOS RETURN TYPE SIGNATURE (Partial<CSSStyleDeclaration>)
    if (!this.startElement) {
      return {};
    }

    // Check if the element is visible in the current vertical viewport
    // Only perform this expensive check if we have visibility handlers (e.g. no check in the widget running without the editor)
    if (this.onHighlightInvisible || this.onHighlightAppear) {
      const wasInvisible = this.isInvisible;
      const isCurrentlyVisible = this.isElementVisibleInViewport(
        this.startElement,
      );

      if (!isCurrentlyVisible) {
        this.isInvisible = true;
        // Only call the callback if this is a new invisibility (not already invisible)
        if (!wasInvisible && this.onHighlightInvisible) {
          this.onHighlightInvisible(highlight.id, this.startElement);
        }
        return this.log(
          new Error(
            `Element is not visible in viewport for highlight with id ${highlight.id}`,
          ),
        );
      } else {
        // Element is now visible (was previously invisible)
        this.isInvisible = false;
        // Call the appear callback if this is a transition from invisible to visible
        if (wasInvisible && this.onHighlightAppear) {
          this.onHighlightAppear(highlight.id, this.startElement);
        }
      }
    } else {
      // If no visibility handlers, assume element is visible to avoid expensive checks
      this.isInvisible = false;
    }
    // If there's no end selector, that means that we want to encircle the whole element, so just copy its bounding client rect.
    let endCoords;
    if (endSelector && endSelector !== startSelector) {
      // Re-find the end element if it's not available or no longer in the DOM
      if (!this.endElement || !window.document.contains(this.endElement)) {
        this.endElement = globalThis.document.querySelector(
          endSelector,
        ) as HTMLElement;
      }
      if (this.endElement) {
        endCoords = getCoords(this.endElement);
      }
    }
    const elementNotFound = !this.startElement;
    if (elementNotFound && highlight.generated) {
      if (this.onHighlightNotFound) {
        this.onHighlightNotFound(
          highlight.id,
          new Error(`Element not found for highlight with id ${highlight.id}`),
        );
        return {};
      }
      return this.log(
        new Error(`Element not found for highlight with id ${highlight.id}`),
      );
      // Fallback to searching by text
      // this.searchElementByText();
    }
    const startCoords = getCoords(this.startElement);
    const startElementSize = this.startElement.getBoundingClientRect();
    const height = endCoords
      ? endCoords.top + endCoords.height - startCoords.top
      : startElementSize.height;
    // The width of the highlight is the maximum width
    const width = Math.max(
      startElementSize.width,
      this.endElement?.getBoundingClientRect().width ?? -Infinity,
    );

    // Create current rect for similarity checking
    const currentRect: HighlightRect = {
      id: this.id,
      top: startCoords.top,
      left: startCoords.left,
      width: width,
      height: height,
      timestamp: Date.now(),
      text: this.highlight.selector.text,
    };

    // Check for similar rects, but allow different text highlights on the same selector
    const similarRect = NotationHighlight.findSimilarRect(
      currentRect,
      this.highlight,
    );
    if (similarRect) {
      console.debug('Similar rect detected:', {
        currentRect,
        similarRect,
        highlightId: this.id,
        similarHighlightId: similarRect.id,
        currentText: this.highlight.selector.text,
        similarText: similarRect.text,
      });

      // Only mark as duplicate if it's the same text content
      if (
        this.highlight.selector.text &&
        similarRect.text &&
        this.highlight.selector.text === similarRect.text
      ) {
        // Mark as not found and call handler
        if (this.onHighlightNotFound && highlight.generated) {
          this.onHighlightNotFound(
            this.id,
            new Error(
              `Similar rect with same text already exists for highlight with id ${this.id}, similar to ${similarRect.id}`,
            ),
          );
          return {};
        }
      }
    }

    // Add current rect to tracking
    NotationHighlight.addRectToTracking(currentRect);

    const style = {
      position: 'absolute',
      top: startCoords.top + 'px',
      left: startCoords.left + 'px',
      height: height + 'px',
      width: width + 'px',
      zIndex: '9999',
      opacity: '0.3',
      pointerEvents: 'none',
    };
    // Adjusting for scrolling lag when highlighting a sticky element (or an element inside a sticky container);
    if (this.stickyParent) {
      const rect = this.stickyParent?.getBoundingClientRect();
      const computedTop = parseInt(getComputedStyle(this.stickyParent).top);
      if (rect.top === computedTop) {
        // Element behaves as fixed, set our highlight to fixed
        style.position = 'fixed';
        // And remove the scroll offset
        const newTop = Math.floor(parseInt(style.top) - window.scrollY);
        if (!this.prevTop) {
          this.prevTop = startCoords.top - window.scrollY;
          style.top = newTop + 'px';
        } else if (Math.abs(newTop - this.prevTop) > 1) {
          // If the difference is more than 1 pixel, adjust the top
          // prevents jank
          // TODO: There's still a slight position issue when scrolling
          // very fast, but we'll have to live with it for now
          this.prevTop = newTop;
          style.top = newTop + 'px';
        } else {
          style.top = this.prevTop + 'px';
        }
      }
    }
    return style;
  }

  private findStickyParent() {
    // recursively go over the parents and find the first sticky parent
    if (!this.startElement) return;
    let parent: HTMLElement | null = this.startElement;
    while (parent) {
      // Might be a bug in JS DOM, so we check on the style as well so the
      // tests pass
      if (
        parent.style.position === 'sticky' ||
        window.getComputedStyle(parent).position === 'sticky'
      ) {
        this.stickyParent = parent;
        return;
      }
      parent = parent.parentElement;
    }
  }

  private isElementVisibleInViewport(element: HTMLElement): boolean {
    // Traverse up the DOM tree to check if any parent is hiding the element
    let currentElement: HTMLElement | null = element;

    while (
      currentElement &&
      currentElement !== globalThis.document.documentElement
    ) {
      const computedStyle = window.getComputedStyle(currentElement);

      // Check for visibility-blocking CSS properties
      if (
        computedStyle.display === 'none' ||
        computedStyle.visibility === 'hidden' ||
        computedStyle.opacity === '0'
      ) {
        console.debug(`Element hidden by parent: ${currentElement.tagName}`, {
          display: computedStyle.display,
          visibility: computedStyle.visibility,
          opacity: computedStyle.opacity,
          highlightId: this.id,
        });
        return false;
      }

      // // Check if any element in the tree has zero dimensions
      // const rect = currentElement.getBoundingClientRect();
      // if (rect.width === 0 || rect.height === 0) {
      //   console.debug(
      //     `Element has zero dimensions: ${currentElement.tagName}`,
      //     {
      //       rect,
      //       highlightId: this.id,
      //       count: this.highlight.counter,
      //     },
      //   );
      //   return false;
      // }

      currentElement = currentElement.parentElement;
    }

    // Check if element is within viewport bounds horizontally
    const rect = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;

    // Element must be at least partially visible horizontally
    const isHorizontallyVisible = rect.left < viewportWidth && rect.right > 0;

    // Check if element has any size (not collapsed)
    const hasSize = rect.width > 1 && rect.height > 1;

    console.debug('Element visibility check:', {
      rect,
      viewportWidth,
      isHorizontallyVisible,
      hasSize,
      highlightId: this.id,
    });

    return isHorizontallyVisible && hasSize;
  }

  /** Check if two rects are similar based on position and size (threshold: 10px) */
  private static isRectSimilar(
    rect1: HighlightRect,
    rect2: HighlightRect,
    threshold = 10,
  ): boolean {
    const positionDiff =
      Math.abs(rect1.top - rect2.top) + Math.abs(rect1.left - rect2.left);
    const sizeDiff =
      Math.abs(rect1.width - rect2.width) +
      Math.abs(rect1.height - rect2.height);

    return positionDiff <= threshold && sizeDiff <= threshold;
  }

  /** Find existing rect similar to current one */
  private static findSimilarRect(
    currentRect: HighlightRect,
    _highlight?: THighlight,
  ): HighlightRect | null {
    return (
      NotationHighlight.highlightRects.find(
        (existingRect) =>
          existingRect.id !== currentRect.id &&
          NotationHighlight.isRectSimilar(existingRect, currentRect),
      ) || null
    );
  }

  /** Add rect to tracking and clean up old ones (5min retention) */
  private static addRectToTracking(rect: HighlightRect): void {
    // Remove old rects (older than 5 minutes) to prevent memory leaks
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
    NotationHighlight.highlightRects = NotationHighlight.highlightRects.filter(
      (r) => r.timestamp > fiveMinutesAgo,
    );

    // Add new rect
    NotationHighlight.highlightRects.push(rect);
  }

  /**
   * Find element by text content using searchWithXPathDeep with semantic hierarchy
   * Prioritizes parentElement matches, then headings, then shortest DOM path
   * Creates text boundaries for partial text matching
   */
  private findElementByTextWithinParent(
    parentElement: HTMLElement,
    textContent: string,
  ): HTMLElement | null {
    console.debug(
      'Trying to find element by text using searchWithXPathDeep',
      textContent,
    );
    try {
      // Use searchWithXPathDeep to find all matching elements
      const matchingElements = searchWithXPathDeep(textContent);

      if (matchingElements.length === 0) {
        console.debug(
          'No elements found with searchWithXPathDeep for content:',
          textContent,
        );
        return null;
      }

      console.debug(
        `Found ${matchingElements.length} matching elements`,
        matchingElements,
      );

      // First, try to match to the parentElement
      const parentMatch = matchingElements.find(
        (element) =>
          element === parentElement || parentElement?.contains(element),
      );

      let targetElement: HTMLElement | null = null;

      if (parentMatch) {
        console.debug('Found element matching parentElement', parentMatch);
        targetElement = parentMatch;
      } else {
        // If no parent match, pick the best element using text length similarity
        targetElement = this.selectLargestVisibleElement(
          matchingElements,
          textContent,
        );
        console.debug(
          'Found best element by text length similarity',
          targetElement,
        );
      }

      if (targetElement) {
        // Check if the text is a partial match within the element
        const elementText = targetElement.textContent || '';
        if (elementText.includes(textContent) && elementText !== textContent) {
          console.debug('Creating text boundaries for partial match');
          // Create text boundaries for the partial text
          const boundaryElement = this.createTextBoundaries(
            targetElement,
            textContent,
          );
          if (boundaryElement) {
            console.debug('Created text boundary element', boundaryElement);
            return boundaryElement;
          }
        }
        return targetElement;
      }

      return null;
    } catch (error) {
      console.error('Error in findElementByTextWithinParent:', error);
      return null;
    }
  }

  /**
   * Select the element with the shortest DOM path from a list of elements
   */
  private selectElementWithShortestPath(elements: HTMLElement[]): HTMLElement {
    if (elements.length === 1) {
      return elements[0];
    }

    return elements.reduce((shortest, current) => {
      const shortestPath = this.getDOMPathLength(shortest);
      const currentPath = this.getDOMPathLength(current);

      return currentPath < shortestPath ? current : shortest;
    }, elements[0]);
  }

  /**
   * Select the best element from a list using visibility, text length similarity, and bounding box size
   * 1. Filter by visibility
   * 2. Sort by text length similarity to search text
   * 3. If multiple candidates have similar text length, select the one with largest bounding box
   */
  private selectLargestVisibleElement(
    elements: HTMLElement[],
    searchText?: string,
  ): HTMLElement | null {
    if (elements.length === 0) {
      return null;
    }

    // Filter by visibility
    const visibleElements = elements.filter((element) => {
      const isVisible = this.isElementVisibleInViewport(element);
      if (!isVisible) {
        console.debug('Skipping invisible element', element);
      }
      return isVisible;
    });

    if (visibleElements.length === 0) {
      console.debug('No visible elements found in the list');
      return null;
    }

    if (visibleElements.length === 1) {
      console.debug('Selected only visible element', visibleElements[0]);
      return visibleElements[0];
    }

    // If no search text provided, fall back to largest bounding box
    if (!searchText) {
      return this.selectByLargestBoundingBox(visibleElements);
    }

    // Sort by text length similarity to search text
    const elementsWithSimilarity = visibleElements.map((element) => {
      const elementText = element.textContent || '';
      const textLengthDiff = Math.abs(elementText.length - searchText.length);
      return { element, textLengthDiff, elementText };
    });

    // Sort by text length difference (closer to search text length is better)
    elementsWithSimilarity.sort((a, b) => a.textLengthDiff - b.textLengthDiff);

    console.debug(
      'Elements sorted by text length similarity:',
      elementsWithSimilarity.map((item) => ({
        tagName: item.element.tagName,
        textLength: item.elementText.length,
        searchTextLength: searchText.length,
        difference: item.textLengthDiff,
      })),
    );

    // Find the best text length difference
    const bestDifference = elementsWithSimilarity[0].textLengthDiff;

    // Get all elements with the best (smallest) text length difference
    const bestCandidates = elementsWithSimilarity
      .filter((item) => item.textLengthDiff === bestDifference)
      .map((item) => item.element);

    if (bestCandidates.length === 1) {
      console.debug(
        'Selected element with best text length similarity',
        bestCandidates[0],
      );
      return bestCandidates[0];
    }

    // If multiple candidates have the same text length similarity, select the one with largest bounding box
    console.debug(
      `Multiple candidates with same text length similarity (${bestDifference}), selecting largest bounding box`,
    );
    return this.selectByLargestBoundingBox(bestCandidates);
  }

  /**
   * Select the element with the largest bounding box from a list of elements
   */
  private selectByLargestBoundingBox(elements: HTMLElement[]): HTMLElement {
    return elements.reduce((largest, current) => {
      const largestRect = largest.getBoundingClientRect();
      const currentRect = current.getBoundingClientRect();

      const largestArea = largestRect.width * largestRect.height;
      const currentArea = currentRect.width * currentRect.height;

      return currentArea > largestArea ? current : largest;
    }, elements[0]);
  }

  /**
   * Calculate the DOM path length from root to element
   */
  private getDOMPathLength(element: HTMLElement): number {
    let length = 0;
    let current: HTMLElement | null = element;

    while (current && current !== window.document.documentElement) {
      length++;
      current = current.parentElement;
    }

    return length;
  }

  /**
   * Fallback search for text in the whole document when no start selector is provided
   * First checks for exact text matches, then partial matches with boundary creation
   */
  private findElementByTextInDocument(textContent: string): HTMLElement | null {
    console.debug(
      'Performing fallback search for text in whole document:',
      textContent,
    );
    try {
      // Use searchWithXPathDeep to search the entire document
      const matchingElements = searchWithXPathDeep(textContent);

      if (matchingElements.length === 0) {
        console.debug('No elements found in document for text:', textContent);
        return null;
      }

      console.debug(
        `Found ${matchingElements.length} matching elements in document`,
        matchingElements,
      );

      // First, check for exact text matches
      const exactMatches = matchingElements.filter((element) => {
        const elementText = element.textContent || '';
        return elementText === textContent;
      });

      if (exactMatches.length > 0) {
        console.debug(`Found ${exactMatches.length} exact text matches`);

        // Pick the best element from exact matches using text length similarity
        const targetElement = this.selectLargestVisibleElement(
          exactMatches,
          textContent,
        );
        console.debug('Found best exact element in document', targetElement);

        return targetElement;
      }

      // If no exact matches, check for partial matches
      console.debug('No exact matches found, checking for partial matches');
      const partialMatches = matchingElements.filter((element) => {
        const elementText = element.textContent || '';
        return elementText.includes(textContent) && elementText !== textContent;
      });

      if (partialMatches.length > 0) {
        console.debug(`Found ${partialMatches.length} partial text matches`);

        // Pick the best element from partial matches using text length similarity
        const targetElement = this.selectLargestVisibleElement(
          partialMatches,
          textContent,
        );
        console.debug('Found best partial element in document', targetElement);

        if (targetElement) {
          console.debug(
            'Creating text boundaries for partial match in document',
          );
          // Create text boundaries for the partial text
          const boundaryElement = this.createTextBoundaries(
            targetElement,
            textContent,
          );
          if (boundaryElement) {
            console.debug(
              'Created text boundary element in document',
              boundaryElement,
            );
            return boundaryElement;
          }
        }
      }

      console.debug('No exact or partial matches found');
      return null;
    } catch (error) {
      console.error('Error in findElementByTextInDocument:', error);
      return null;
    }
  }

  /**
   * Create a single span element around the found text
   */
  private createTextBoundaries(
    parentElement: HTMLElement,
    searchText: string,
  ): HTMLElement | null {
    // Get the full text content of the parent element
    const fullText = parentElement.textContent || '';
    const textIndex = fullText.indexOf(searchText);

    if (textIndex === -1) {
      console.debug('Text not found in parent element:', searchText);
      return null;
    }

    // Find the text node and position where the search text starts
    const textNodeAndOffset = this.findTextNodeAndOffset(
      parentElement,
      textIndex,
    );
    if (!textNodeAndOffset) {
      console.debug('Could not find text node at offset:', textIndex);
      return null;
    }

    const { textNode, offset } = textNodeAndOffset;
    const endOffset = offset + searchText.length;

    // Create a single span element
    const textSpan = window.document.createElement('span');
    textSpan.setAttribute('data-text-highlight', 'true');
    textSpan.setAttribute('data-highlight-text', searchText);
    textSpan.style.position = 'relative';
    textSpan.style.display = 'inline';

    try {
      // Split the text node at the start position
      const beforeText = textNode.textContent?.substring(0, offset) || '';
      const targetText =
        textNode.textContent?.substring(offset, endOffset) || '';
      const afterText = textNode.textContent?.substring(endOffset) || '';

      // Create new text nodes
      const beforeNode = window.document.createTextNode(beforeText);
      const targetNode = window.document.createTextNode(targetText);
      const afterNode = window.document.createTextNode(afterText);

      // Insert the target text inside the span
      textSpan.appendChild(targetNode);

      // Insert the span and text nodes
      const parent = textNode.parentElement;
      if (!parent) return null;

      // Replace the original text node with the new structure
      parent.insertBefore(beforeNode, textNode);
      parent.insertBefore(textSpan, textNode);
      parent.insertBefore(afterNode, textNode);
      parent.removeChild(textNode);

      // Return the span as the highlight target
      return textSpan;
    } catch (error) {
      console.error('Error creating text span:', error);
      return null;
    }
  }

  /**
   * Find the text node and offset for a given character position
   */
  private findTextNodeAndOffset(
    element: HTMLElement,
    targetOffset: number,
  ): { textNode: Text; offset: number } | null {
    let currentOffset = 0;
    const walker = window.document.createTreeWalker(
      element,
      NodeFilter.SHOW_TEXT,
      null,
    );

    let node: Text | null;
    while ((node = walker.nextNode() as Text)) {
      const nodeLength = node.textContent?.length || 0;
      if (currentOffset + nodeLength > targetOffset) {
        return {
          textNode: node,
          offset: targetOffset - currentOffset,
        };
      }
      currentOffset += nodeLength;
    }

    return null;
  }

  private setStyle(style: Partial<CSSStyleDeclaration>) {
    const elementStyle: Partial<CSSStyleDeclaration | undefined> =
      this.element?.style;
    if (!elementStyle) {
      this.log('Element style not found');
      return;
    }

    // If style is empty (e.g., when highlight is invisible), don't apply any positioning
    if (
      !style?.position &&
      !style?.left &&
      !style?.top &&
      !style?.height &&
      !style?.width
    ) {
      this.log('Empty style object, skipping style application');
      return;
    }

    elementStyle.position = style.position;
    elementStyle.top = '0';
    elementStyle.left = '0';
    elementStyle.transform = `translate(${style.left ?? '0'}, ${style.top ?? '0'})`;
    elementStyle.height = style.height;
    elementStyle.width = style.width;
    elementStyle.zIndex = style.zIndex;
    elementStyle.pointerEvents = style.pointerEvents;
    // We include the padding of the highlighted element in the wrapper dimensions
    elementStyle.padding = '0';
    elementStyle.margin = '0';
    elementStyle.background = 'transparent';
    elementStyle.paddingTop = '10px';
    elementStyle.borderTopLeftRadius = '0px';
  }

  public createHighlightElement() {
    const { highlight, config } = this;
    const startSelector = highlight.selector?.start;
    const textContent = highlight.selector?.text;
    let style: Partial<CSSStyleDeclaration> = {};
    if (highlight.inline) {
      this.element = window.document.querySelector(
        escapeCSS(startSelector),
      ) as HTMLElement;
    } else {
      let startElement: HTMLElement | null = null;

      // If we have a start selector, try to find the parent element first
      if (startSelector) {
        const escapedSelector = escapeCSS(startSelector);
        // First try with the intact selector
        let parentElement = window.document.querySelector(
          escapedSelector,
        ) as HTMLElement;

        // If not found and selector contains nth-of-type, try without it
        if (!parentElement && /:nth-of-type\((\d+)\)/.test(escapedSelector)) {
          parentElement = window.document.querySelector(
            // remove the nth-of-type selector if it exists
            // For the most part it's redundant as we should always be able
            // to find the element by text
            escapedSelector.replace(/:nth-of-type\((\d+)\)/, ''),
          ) as HTMLElement;
        }

        if (parentElement) {
          console.debug(
            'Found parent element by selector in createHighlightElement',
            parentElement,
          );

          // If we have text content, search for it within the parent element
          if (textContent) {
            const textElement = this.findElementByTextWithinParent(
              parentElement,
              textContent,
            );
            if (textElement) {
              startElement = textElement;
              console.debug(
                'Found element by text within parent in createHighlightElement',
                startElement,
                startSelector,
              );
            } else {
              // handle text not found - fallback to using the parent element itself
              startElement = parentElement;
              console.debug(
                'Text not found, using parent element as fallback in createHighlightElement',
                startElement,
              );
            }
          } else {
            // No text content, use the parent element directly
            startElement = parentElement;
            console.debug(
              'No text content, using parent element in createHighlightElement',
              startElement,
            );
          }
        }
      }
      if (textContent && !startElement) {
        // No start element found at but we have text content, search directly for the text
        console.debug(
          'No start selector provided, searching directly for text in createHighlightElement:',
          textContent,
        );
        const textElement = this.findElementByTextInDocument(textContent);
        if (textElement) {
          startElement = textElement;
          console.debug(
            'Found element by direct text search in createHighlightElement',
            startElement,
          );
        }
      }

      if (!startElement && highlight.generated) {
        if (this.onHighlightNotFound) {
          this.onHighlightNotFound(
            highlight.id,
            new Error(
              `Element not found for highlight with id ${highlight.id}`,
            ),
          );
          return;
        }
        return this.log(
          new Error(`Element not found for highlight with id ${highlight.id}`),
        );
      }
      if (startElement) {
        startElement.setAttribute('data-highlight-id', highlight.id);
        style = this.calculatePosition(startElement);
        this.startElement = startElement;
      }

      // Always create highlight element, even if invisible (it might become visible later)
      const wrapper = window.document.createElement('div');
      // create style to set higher z-index on all rough notation (unless already created previously)
      const existingStyle = globalThis.document.getElementById(
        'rough-notation-z-index',
      );
      if (!existingStyle) {
        const style = window.document.createElement('style');
        style.id = 'rough-notation-z-index';
        style.innerHTML = `
        .rough-annotation, .rough-notation path {
          z-index: ${config?.styles?.max_zindex ?? 99999};
        }
      `;
        window.document.head.appendChild(style);
      }
      this.element = wrapper;
      this.findStickyParent();
      this.setStyle(style); // THIS IS NEEDED OTHERWISE HIGHLIGHTS WILL NOT BE DISPLAYED, THEY WILL EXIST BUT NO STYLE WILL BE APPLIED
      if (config?.debug) {
        wrapper.style.border = '3px solid red';
      }
      window.document.body.appendChild(wrapper);
      const className = NotationHighlight.classNamePrefix + highlight.id;
      wrapper.classList.add(className);
      //  create ids
      if (this.isEditor) {
        const highlightIdElement = window.document.createElement('div');
        highlightIdElement.classList.add(
          NotationHighlight.classNamePrefix + 'id',
        );
        highlightIdElement.innerText = this.counter?.toString() ?? '';
        this.idElements.push(highlightIdElement);
        wrapper.appendChild(highlightIdElement);
      }
    }

    // Convert the annotationOptions from the backend into the native camelCase to be used by the rough annotation lib
    const annotationOptionsFromCuepoint: Record<string, unknown> = {};
    let annotationOptionsFromHighlight;
    if (
      this.cuepoint &&
      ('rough_notation' in this.cuepoint || 'roughNotation' in this.cuepoint)
    ) {
      Object.entries(
        this.cuepoint?.['rough_notation'] ??
          this.cuepoint?.['roughNotation'] ??
          {},
      ).forEach(
        ([key, value]) =>
          (annotationOptionsFromCuepoint[camelCase(key)] = value),
      );
    } else if (this.highlight.annotationOptions) {
      annotationOptionsFromHighlight = this.highlight.annotationOptions;
    }
    const annotationOptions = {
      ...DEFAULT_ANNOTATION_OPTIONS,
      ...highlight.annotation_options,
      ...annotationOptionsFromHighlight,
      ...annotationOptionsFromCuepoint,
    };

    this.configureAnnotationOptions(highlight, annotationOptions, config);
  }
  private configureAnnotationOptions(
    highlight: THighlight,
    annotationOptions,
    config?: BaseConfig,
  ) {
    highlight.annotation_options = annotationOptions;
    if (this.element === undefined) throw new Error('Element not found');
    if (highlight.annotation_options?.color === 'primary' && config?.colors) {
      annotationOptions.color = config.colors.primary;
    } else if (
      highlight.annotation_options?.color === 'secondary' &&
      config?.colors
    ) {
      annotationOptions.color = config.colors.secondary;
    }
    annotationOptions.stroke_width = annotationOptions.stroke_width || 10;
    annotationOptions.padding = annotationOptions.padding || 5;
    annotationOptions.roughness = 0;
    // annotationOptions.type = 'box';
    // alternate with forced boxes if larger than half screen width DLY
    if (
      this.config?.bracket_override_enabled &&
      ((Number.parseInt(this.element.style.width) >
        globalThis.document.body.clientWidth / 2 &&
        Number.parseInt(this.element.style.height) > 30) ||
        Number.parseInt(this.element.style.height) > 45) && // high areas
      !annotationOptions.protected_type
    ) {
      annotationOptions.type = 'bracket';
      annotationOptions.brackets = ['right', 'left'];
    }
    if (
      annotationOptions.color.length === 7 &&
      annotationOptions.type === 'highlight'
    ) {
      annotationOptions.color += '22'; // force low opacity
    }

    if (config?.debug) console.debug(annotationOptions);
    highlight.annotation_options = annotationOptions;

    // don't render twice the same highlight rough annotation (it looks bad)
    if (NotationHighlight.shownHighlights.has(this.highlight.selector.start))
      return;
    NotationHighlight.shownHighlights.add(this.highlight.selector.start);

    if (this.options?.createAnnotations !== false) {
      this.annotation = annotate(this.element, annotationOptions);
      this.highlight.annotation_options = annotationOptions;
      this.redraw = () => {
        this.annotation?.remove();
        if (!this.element) throw new Error('Element not found');
        if (!this.highlight.annotation_options)
          throw new Error('Annotation options not found ');
        this.annotation = annotate(this.element, this.highlight.annotation_options);
        this.annotation.show();
      };
    }
  }
  private log(message: Error): never;
  private log(message: string): void;
  private log(message: string | Error): void | never {
    if (typeof message === 'string') {
      console.debug(message);
    } else if (message instanceof Error) {
      return this.logError(message);
    }
  }
  // @ts-expect-error fix never here by either throwing or changin the type
  private logError(error: Error): never {
    if (this.config?.debug) {
      console.debug(error);
    }
    // throw error;
  }
}
export function escapeCSS(string: string) {
  if (!string) return string;

  // Escape all non-alphanumeric ASCII characters (using ranges). Won't
  // match # > : and other special characters that have meaning in CSS
  const regexp = /([:!$-'*-,/;<?@^`{-~])/g;
  return string
    .replace(regexp, '\\$1')
    .replace(/\\:(nth-of-type|nth-child)/g, ':$1');
}
